import React, { useState } from 'react';
import { 
  Database, 
  CheckCircle, 
  AlertCircle, 
  Play, 
  Package,
  CreditCard,
  FileText,
  History
} from 'lucide-react';
import { 
  collection, 
  addDoc, 
  serverTimestamp,
  getDocs,
  query,
  limit
} from 'firebase/firestore';
import { appDb, ensureAppAuthentication } from '../appFirebaseClient';

const CollectionInitializer = () => {
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [collectionsStatus, setCollectionsStatus] = useState({});
  const [prodBootstrapAck, setProdBootstrapAck] = useState('');

  const isProductionHost =
    typeof window !== 'undefined' &&
    (window.location.hostname === 'backoffice.acctoo.com' ||
      window.location.hostname === 'backoffice-acctoo.web.app');
  const prodInitializerLocked = isProductionHost && prodBootstrapAck !== 'INITIALIZE_PRODUCTION';

  // Initial top-up catalog data
  const initialTopups = [
    {
      name: 'Operations Mini Pack',
      description: 'Additional 1,000 operations for immediate use',
      price: 99,
      currency: '₹',
      category: 'operations',
      displayOrder: 1,
      isActive: true,
      limits: {
        firebase_operations: 1000,
        storage_mb: 0,
        sales_documents: 0,
        parties: 0
      },
      features: [
        '1,000 additional operations',
        'Till-use (never expires)',
        'Instant activation'
      ],
      popular: false
    },
    {
      name: 'Operations Standard Pack',
      description: 'Additional 5,000 operations for heavy usage',
      price: 399,
      currency: '₹',
      category: 'operations',
      displayOrder: 2,
      isActive: true,
      limits: {
        firebase_operations: 5000,
        storage_mb: 0,
        sales_documents: 0,
        parties: 0
      },
      features: [
        '5,000 additional operations',
        'Till-use (never expires)',
        'Best value for money',
        'Instant activation'
      ],
      popular: true
    },
    {
      name: 'Operations Mega Pack',
      description: 'Additional 15,000 operations for power users',
      price: 999,
      currency: '₹',
      category: 'operations',
      displayOrder: 3,
      isActive: true,
      limits: {
        firebase_operations: 15000,
        storage_mb: 0,
        sales_documents: 0,
        parties: 0
      },
      features: [
        '15,000 additional operations',
        'Till-use (never expires)',
        'Maximum value',
        'Instant activation'
      ],
      popular: false
    },
    {
      name: 'Storage Basic Pack',
      description: 'Additional 500MB storage space',
      price: 199,
      currency: '₹',
      category: 'storage',
      displayOrder: 4,
      isActive: true,
      limits: {
        firebase_operations: 0,
        storage_mb: 500,
        sales_documents: 0,
        parties: 0
      },
      features: [
        '500MB additional storage',
        'Till-use (never expires)',
        'Instant activation'
      ],
      popular: false
    },
    {
      name: 'Storage Premium Pack',
      description: 'Additional 2GB storage space',
      price: 599,
      currency: '₹',
      category: 'storage',
      displayOrder: 5,
      isActive: true,
      limits: {
        firebase_operations: 0,
        storage_mb: 2048,
        sales_documents: 0,
        parties: 0
      },
      features: [
        '2GB additional storage',
        'Till-use (never expires)',
        'Best value for storage',
        'Instant activation'
      ],
      popular: true
    },
    {
      name: 'Documents Basic Pack',
      description: 'Additional 1,000 sales documents',
      price: 299,
      currency: '₹',
      category: 'documents',
      displayOrder: 6,
      isActive: true,
      limits: {
        firebase_operations: 0,
        storage_mb: 0,
        sales_documents: 1000,
        parties: 0
      },
      features: [
        '1,000 additional sales documents',
        'Till-use (never expires)',
        'Instant activation'
      ],
      popular: false
    },
    {
      name: 'Documents Premium Pack',
      description: 'Additional 5,000 sales documents',
      price: 1199,
      currency: '₹',
      category: 'documents',
      displayOrder: 7,
      isActive: true,
      limits: {
        firebase_operations: 0,
        storage_mb: 0,
        sales_documents: 5000,
        parties: 0
      },
      features: [
        '5,000 additional sales documents',
        'Till-use (never expires)',
        'Best value for documents',
        'Instant activation'
      ],
      popular: true
    },
    {
      name: 'Parties Basic Pack',
      description: 'Additional 500 parties/customers',
      price: 199,
      currency: '₹',
      category: 'parties',
      displayOrder: 8,
      isActive: true,
      limits: {
        firebase_operations: 0,
        storage_mb: 0,
        sales_documents: 0,
        parties: 500
      },
      features: [
        '500 additional parties/customers',
        'Till-use (never expires)',
        'Instant activation'
      ],
      popular: false
    },
    {
      name: 'Parties Premium Pack',
      description: 'Additional 2,000 parties/customers',
      price: 699,
      currency: '₹',
      category: 'parties',
      displayOrder: 9,
      isActive: true,
      limits: {
        firebase_operations: 0,
        storage_mb: 0,
        sales_documents: 0,
        parties: 2000
      },
      features: [
        '2,000 additional parties/customers',
        'Till-use (never expires)',
        'Best value for parties',
        'Instant activation'
      ],
      popular: true
    },
    {
      name: 'Operations + Storage Combo',
      description: '3,000 operations + 1GB storage',
      price: 799,
      currency: '₹',
      category: 'combo',
      displayOrder: 10,
      isActive: true,
      limits: {
        firebase_operations: 3000,
        storage_mb: 1024,
        sales_documents: 0,
        parties: 0
      },
      features: [
        '3,000 additional operations',
        '1GB additional storage',
        'Till-use (never expires)',
        'Save ₹200 vs buying separately',
        'Instant activation'
      ],
      popular: true
    },
    {
      name: 'Business Boost Pack',
      description: 'Complete business solution with all resources',
      price: 1999,
      currency: '₹',
      category: 'combo',
      displayOrder: 11,
      isActive: true,
      limits: {
        firebase_operations: 10000,
        storage_mb: 2048,
        sales_documents: 2000,
        parties: 1000
      },
      features: [
        '10,000 additional operations',
        '2GB additional storage',
        '2,000 additional sales documents',
        '1,000 additional parties',
        'Till-use (never expires)',
        'Save ₹1,000 vs buying separately',
        'Instant activation'
      ],
      popular: true
    },
    {
      name: 'Enterprise Operations Pack',
      description: '50,000 operations for enterprise users',
      price: 3999,
      currency: '₹',
      category: 'enterprise',
      displayOrder: 12,
      isActive: true,
      limits: {
        firebase_operations: 50000,
        storage_mb: 0,
        sales_documents: 0,
        parties: 0
      },
      features: [
        '50,000 additional operations',
        'Till-use (never expires)',
        'Enterprise-grade capacity',
        'Instant activation',
        'Priority support'
      ],
      popular: false
    },
    {
      name: 'Enterprise Storage Pack',
      description: '10GB storage for enterprise users',
      price: 2999,
      currency: '₹',
      category: 'enterprise',
      displayOrder: 13,
      isActive: true,
      limits: {
        firebase_operations: 0,
        storage_mb: 10240,
        sales_documents: 0,
        parties: 0
      },
      features: [
        '10GB additional storage',
        'Till-use (never expires)',
        'Enterprise-grade capacity',
        'Instant activation',
        'Priority support'
      ],
      popular: false
    }
  ];

  const checkCollectionsStatus = async () => {
    try {
      setLoading(true);
      
      // Ensure proper authentication
      await ensureAppAuthentication();
      
      const collections = ['topup_catalog', 'topup_purchases', 'payment_history'];
      const status = {};

      for (const collectionName of collections) {
        try {
          const q = query(collection(appDb, collectionName), limit(1));
          const snapshot = await getDocs(q);
          status[collectionName] = {
            exists: true,
            count: snapshot.size,
            error: null
          };
        } catch (error) {
          status[collectionName] = {
            exists: false,
            count: 0,
            error: error.message
          };
        }
      }

      setCollectionsStatus(status);
    } catch (error) {
      console.error('Error checking collections status:', error);
      setMessage({ type: 'error', text: 'Failed to check collections status' });
    } finally {
      setLoading(false);
    }
  };

  const initializeCollections = async () => {
    try {
      if (prodInitializerLocked) {
        setMessage({
          type: 'error',
          text: 'Production environment: type INITIALIZE_PRODUCTION in the acknowledgement field to unlock.'
        });
        return;
      }
      if (isProductionHost) {
        const ok = window.confirm(
          'This will write sample/top-up seed documents to the connected production app database. Have you taken a backup? Continue?'
        );
        if (!ok) return;
      }

      setLoading(true);
      setMessage({ type: '', text: '' });

      // Ensure proper authentication
      await ensureAppAuthentication();
      
      // Add a small delay to ensure authentication is fully established
      await new Promise(resolve => setTimeout(resolve, 500));

      // 1. Create topup_catalog collection
      console.log('📦 Creating topup_catalog collection...');
      const topupResults = [];
      
      for (const topup of initialTopups) {
        const topupData = {
          ...topup,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp()
        };
        
        const docRef = await addDoc(collection(appDb, 'topup_catalog'), topupData);
        topupResults.push({ id: docRef.id, name: topup.name });
        console.log(`✅ Created top-up: ${topup.name} (ID: ${docRef.id})`);
      }

      // 2. Create topup_purchases collection
      console.log('💰 Creating topup_purchases collection...');
      const samplePurchase = {
        companyId: 'sample_company',
        topupId: topupResults[0].id,
        amount: 99,
        currency: '₹',
        paymentMethod: 'manual',
        paymentId: 'sample_payment_001',
        status: 'completed',
        purchasedAt: serverTimestamp(),
        createdAt: serverTimestamp(),
        notes: 'Sample purchase for collection initialization'
      };
      
      await addDoc(collection(appDb, 'topup_purchases'), samplePurchase);

      // 3. Migration logs collection removed - no longer needed

      // 3. Create payment_history collection
      console.log('💳 Creating payment_history collection...');
      const samplePayment = {
        companyId: 'sample_company',
        amount: 399,
        currency: '₹',
        type: 'plan_upgrade',
        method: 'stripe',
        paymentId: 'sample_payment_002',
        status: 'completed',
        processedAt: serverTimestamp(),
        createdAt: serverTimestamp(),
        notes: 'Sample payment for collection initialization'
      };
      
      await addDoc(collection(appDb, 'payment_history'), samplePayment);

      setMessage({ 
        type: 'success', 
        text: `Successfully initialized all collections! Created ${topupResults.length} top-ups.` 
      });

      // Refresh status
      await checkCollectionsStatus();

    } catch (error) {
      console.error('Error initializing collections:', error);
      setMessage({ type: 'error', text: `Failed to initialize collections: ${error.message}` });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Collection Initializer</h1>
          <p className="text-gray-600 mt-1">Initialize Firebase collections for the top-up system</p>
        </div>
        <button
          onClick={checkCollectionsStatus}
          disabled={loading}
          className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2 disabled:opacity-50"
        >
          <Database className="w-5 h-5" />
          Check Status
        </button>
      </div>

      {/* Message */}
      {message.text && (
        <div className={`p-4 rounded-lg flex items-center gap-2 ${
          message.type === 'success' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
        }`}>
          {message.type === 'success' ? <CheckCircle className="w-5 h-5" /> : <AlertCircle className="w-5 h-5" />}
          <span>{message.text}</span>
        </div>
      )}

      {/* Collections Status */}
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-xl font-bold text-gray-900 mb-4">Collections Status</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {Object.entries(collectionsStatus).map(([collectionName, status]) => (
            <div key={collectionName} className="flex items-center justify-between p-4 border rounded-lg">
              <div className="flex items-center gap-3">
                {collectionName === 'topup_catalog' && <Package className="w-5 h-5 text-blue-600" />}
                {collectionName === 'topup_purchases' && <CreditCard className="w-5 h-5 text-green-600" />}
                {/* Migration logs icon removed */}
                {collectionName === 'payment_history' && <History className="w-5 h-5 text-orange-600" />}
                <div>
                  <p className="font-medium text-gray-900">{collectionName}</p>
                  <p className="text-sm text-gray-500">
                    {status.exists ? `${status.count} documents` : 'Not found'}
                  </p>
                </div>
              </div>
              <div className={`px-2 py-1 rounded-full text-xs font-medium ${
                status.exists 
                  ? 'bg-green-100 text-green-800' 
                  : 'bg-red-100 text-red-800'
              }`}>
                {status.exists ? 'Ready' : 'Missing'}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Initialize Button */}
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-xl font-bold text-gray-900 mb-4">Initialize Collections</h2>
        <p className="text-gray-600 mb-4">
          This will create the following collections with initial data:
        </p>
        <ul className="list-disc list-inside text-gray-600 mb-6 space-y-1">
          <li><strong>topup_catalog</strong> - 13 pre-configured top-up packages</li>
          <li><strong>topup_purchases</strong> - Sample purchase record</li>
          {/* Migration logs description removed */}
          <li><strong>payment_history</strong> - Sample payment record</li>
        </ul>
        {isProductionHost && (
          <div className="mb-4 space-y-2">
            <p className="text-sm text-amber-900 bg-amber-50 border border-amber-200 rounded p-3">
              Production host detected. Initialization is blocked until you type <code className="font-mono">INITIALIZE_PRODUCTION</code> below
              and confirm the dialog. Prefer staging or a dry-run export first.
            </p>
            <label className="block text-sm text-gray-700">
              Acknowledgement
              <input
                className="mt-1 w-full border rounded px-3 py-2 font-mono"
                value={prodBootstrapAck}
                onChange={(e) => setProdBootstrapAck(e.target.value)}
                placeholder="INITIALIZE_PRODUCTION"
                autoComplete="off"
              />
            </label>
          </div>
        )}
        <button
          onClick={initializeCollections}
          disabled={loading || prodInitializerLocked}
          className="bg-green-600 text-white px-6 py-3 rounded-lg hover:bg-green-700 transition-colors flex items-center gap-2 disabled:opacity-50"
        >
          <Play className="w-5 h-5" />
          {loading ? 'Initializing...' : 'Initialize Collections'}
        </button>
      </div>

      {/* Loading Overlay */}
      {loading && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-sm w-full mx-4">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
              <p className="text-gray-600">Initializing collections...</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CollectionInitializer;
